import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const tassClient = axios.create({
  baseURL: API_BASE_URL
});

const validateToken = (token) => {
  if (!token) {
    console.error('[TassAPI] No authentication token provided');
    throw new Error('Authentication required. Please log in.');
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    console.error('[TassAPI] Invalid token format');
    throw new Error('Invalid authentication token. Please log in again.');
  }
  return true;
};

// --- Phase A: Priority Engine ---

export const getPriorities = async (token) => {
  try {
    validateToken(token);
    const response = await tassClient.get('/tass/priorities', {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] getPriorities error:', error);
    throw error;
  }
};

export const getPriorityDetails = async (clientId, token) => {
  try {
    validateToken(token);
    const response = await tassClient.get(`/tass/priorities/${clientId}`, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] getPriorityDetails error:', error);
    throw error;
  }
};

export const setOverride = async (clientId, overrideType, note, token) => {
  try {
    validateToken(token);
    const response = await tassClient.put(`/tass/priorities/${clientId}/override`, {
      overrideType,
      note
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] setOverride error:', error);
    throw error;
  }
};

export const getPriorityBrief = async (token) => {
  try {
    validateToken(token);
    const response = await tassClient.get('/tass/priority-brief', {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] getPriorityBrief error:', error);
    throw error;
  }
};

export const dismissBrief = async (token) => {
  try {
    validateToken(token);
    const response = await tassClient.post('/tass/priority-brief/dismiss', {}, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] dismissBrief error:', error);
    throw error;
  }
};

export const deferPriorityItem = async (clientId, reason, token) => {
  try {
    validateToken(token);
    const response = await tassClient.post(`/tass/priorities/${clientId}/defer`, {
      reason
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] deferPriorityItem error:', error);
    throw error;
  }
};

export const markClientReviewed = async (clientId, token) => {
  try {
    validateToken(token);
    const response = await tassClient.post(`/tass/client/${clientId}/reviewed`, {}, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] markClientReviewed error:', error);
    throw error;
  }
};

export const updateViewContext = async (sessionId, viewContext, token) => {
  try {
    validateToken(token);
    const response = await tassClient.patch(`/tass/sessions/${sessionId}/view-context`, {
      viewContext
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] updateViewContext error:', error);
    throw error;
  }
};

// --- Phase B: Conversational Layer ---

export const createSession = async (viewContext, token, proxyTherapistId = null) => {
  try {
    validateToken(token);
    const body = { viewContext };
    if (proxyTherapistId) body.proxyTherapistId = proxyTherapistId;
    const response = await tassClient.post('/tass/sessions', body, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] createSession error:', error);
    throw error;
  }
};

export const getActiveSession = async (token) => {
  try {
    validateToken(token);
    const response = await tassClient.get('/tass/sessions/active', {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] getActiveSession error:', error);
    throw error;
  }
};

export const sendMessage = async (sessionId, message, token) => {
  try {
    validateToken(token);
    const response = await tassClient.post(`/tass/sessions/${sessionId}/message`, {
      message
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] sendMessage error:', error);
    throw error;
  }
};

export const closeSession = async (sessionId, token) => {
  try {
    validateToken(token);
    const response = await tassClient.post(`/tass/sessions/${sessionId}/close`, {}, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] closeSession error:', error);
    throw error;
  }
};

export const submitFeedback = async (sessionId, messageIndex, taskIndex, feedback, token) => {
  try {
    validateToken(token);
    const response = await tassClient.post(`/tass/sessions/${sessionId}/feedback`, {
      messageIndex,
      taskIndex,
      feedback
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] submitFeedback error:', error);
    throw error;
  }
};

export const confirmTool = async (sessionId, toolCallId, approved, token) => {
  try {
    validateToken(token);
    const response = await tassClient.post(`/tass/sessions/${sessionId}/confirm-tool`, {
      toolCallId,
      approved
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[TassAPI] confirmTool error:', error);
    throw error;
  }
};
